// pages/AdminLogin.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Secret admin entry page — only reachable via the VITE_ADMIN_ENTRY_SLUG URL.
//
// Two-step login (MFA FIRST):
//   Step 1 — Secret access key, validated server-side (ADMIN_MFA_ANSWER env var)
//             The answer is never in the browser bundle.
//   Step 2 — Supabase email + password (only shown after key is correct)
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

const MONO   = "'JetBrains Mono', monospace";
const SANS   = "'Inter', sans-serif";
const PURPLE = "#7c3aed";

const MFA_QUESTION =
  import.meta.env.VITE_ADMIN_MFA_QUESTION || "Enter the system access key";

function useIsOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const dn = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", dn);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", dn); };
  }, []);
  return online;
}

type Step = "mfa" | "credentials";

export default function AdminLogin() {
  const navigate = useNavigate();
  const isOnline = useIsOnline();

  const [step,      setStep]      = useState<Step>("mfa");

  // Step 1 — MFA
  const [mfaAnswer, setMfaAnswer] = useState("");
  const [showKey,   setShowKey]   = useState(false);
  const mfaRef = useRef<HTMLInputElement>(null);

  // Step 2 — Credentials
  const [email,     setEmail]     = useState("");
  const [password,  setPassword]  = useState("");
  const [showPw,    setShowPw]    = useState(false);

  // Shared
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [shake,     setShake]     = useState(false);

  // Redirect only if session exists AND MFA nonce is in sessionStorage from this session
  useEffect(() => {
    if (!sessionStorage.getItem("admin_mfa_nonce")) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      supabase.from("profiles").select("role").eq("id", session.user.id).single()
        .then(({ data }) => { if (data?.role === "admin") navigate("/admin", { replace: true }); });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 480);
  };

  // ── Step 1: Validate access key ─────────────────────────────────────────
  const handleMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaAnswer.trim()) { setError("Enter the access key."); triggerShake(); return; }

    setLoading(true);
    setError("");

    try {
      const res  = await fetch("/api/admin-verify-mfa", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ answer: mfaAnswer.trim() }),
      });
      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(json.error ?? "Incorrect access key.");
        setMfaAnswer("");
        setLoading(false);
        triggerShake();
        return;
      }

      // Store the nonce — proves MFA was passed in this browser session
      sessionStorage.setItem("admin_mfa_nonce", json.nonce ?? "1");
      setMfaAnswer("");
      setLoading(false);
      setStep("credentials");
      setTimeout(() => document.getElementById("admin-email-input")?.focus(), 80);

    } catch {
      setError("Network error. Check your connection.");
      setLoading(false);
      triggerShake();
    }
  };

  // ── Step 2: Email + password ────────────────────────────────────────────
  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError("Enter your email and password."); triggerShake(); return; }
    if (!sessionStorage.getItem("admin_mfa_nonce")) {
      // Nonce missing — send back to step 1
      setStep("mfa");
      setError("Access key expired. Please verify again.");
      setTimeout(() => mfaRef.current?.focus(), 80);
      return;
    }

    setLoading(true);
    setError("");

    const { data, error: authErr } = await supabase.auth.signInWithPassword({
      email:    email.trim().toLowerCase(),
      password,
    });

    if (authErr || !data.session) {
      setError("Invalid credentials.");
      setLoading(false);
      triggerShake();
      return;
    }

    const { data: prof } = await supabase
      .from("profiles").select("role").eq("id", data.session.user.id).single();

    if (prof?.role !== "admin") {
      await supabase.auth.signOut();
      sessionStorage.removeItem("admin_mfa_nonce");
      setError("Access denied.");
      setLoading(false);
      triggerShake();
      return;
    }

    navigate("/admin", { replace: true });
  };

  const goBackToMfa = async () => {
    await supabase.auth.signOut();
    sessionStorage.removeItem("admin_mfa_nonce");
    setStep("mfa");
    setEmail("");
    setPassword("");
    setError("");
    setTimeout(() => mfaRef.current?.focus(), 80);
  };

  const stepIndex = step === "mfa" ? 0 : 1;
  const steps: { key: Step; label: string }[] = [
    { key: "mfa",         label: "ACCESS KEY"   },
    { key: "credentials", label: "CREDENTIALS"  },
  ];

  return (
    <div style={{
      minHeight: "100svh", background: "#070b12",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, fontFamily: SANS,
    }}>
      <style>{`
        @keyframes shake {
          0%,100%{transform:translateX(0)}
          20%{transform:translateX(-7px)} 40%{transform:translateX(7px)}
          60%{transform:translateX(-4px)} 80%{transform:translateX(4px)}
        }
        @keyframes fadeUp {
          from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)}
        }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes stepIn {
          from{opacity:0;transform:translateX(18px)} to{opacity:1;transform:translateX(0)}
        }
        .admin-login-box { animation: fadeUp 0.35s cubic-bezier(0.16,1,0.3,1) both; }
        .admin-login-box.shaking { animation: shake 0.45s ease; }
        .step-content { animation: stepIn 0.25s cubic-bezier(0.16,1,0.3,1) both; }
        .admin-input {
          width:100%; box-sizing:border-box;
          background:rgba(255,255,255,0.04);
          border:1px solid rgba(139,92,246,0.18); border-radius:10px;
          padding:12px 14px; color:#f1f5f9;
          font-family:${MONO}; font-size:14px;
          outline:none; transition:border-color 0.15s,box-shadow 0.15s;
        }
        .admin-input:focus {
          border-color:rgba(139,92,246,0.55);
          box-shadow:0 0 0 3px rgba(139,92,246,0.12);
        }
        .admin-input::placeholder { color:rgba(255,255,255,0.2); }
      `}</style>

      <div
        className={`admin-login-box${shake ? " shaking" : ""}`}
        style={{
          width: "100%", maxWidth: 380,
          background: "linear-gradient(145deg,#0d1117,#111827)",
          border: "1px solid rgba(139,92,246,0.22)",
          borderRadius: 20, padding: "36px 30px 30px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.6),0 0 0 1px rgba(139,92,246,0.08)",
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 26 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: "linear-gradient(135deg,#4c1d95,#7c3aed)",
            border: "1px solid rgba(139,92,246,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 24, margin: "0 auto 14px",
            boxShadow: "0 8px 24px rgba(124,58,237,0.35)",
          }}>🛡️</div>
          <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 20, color: "#f1f5f9", letterSpacing: "-0.03em", marginBottom: 5 }}>
            Admin Access
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", fontFamily: MONO }}>
            Restricted — authorised personnel only
          </div>
        </div>

        {/* Step indicator */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 24 }}>
          {steps.map((s, i) => {
            const done    = i < stepIndex;
            const current = i === stepIndex;
            return (
              <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                  background: done ? "#22c55e" : current ? PURPLE : "rgba(255,255,255,0.06)",
                  border: `1.5px solid ${done ? "#22c55e" : current ? PURPLE : "rgba(255,255,255,0.1)"}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontFamily: MONO, fontWeight: 700,
                  color: (done || current) ? "#fff" : "rgba(255,255,255,0.25)",
                  transition: "all 0.3s",
                }}>
                  {done ? "✓" : i + 1}
                </div>
                <span style={{ fontSize: 10, fontFamily: MONO, letterSpacing: "0.05em", color: current ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.2)" }}>
                  {s.label}
                </span>
                {i < steps.length - 1 && (
                  <div style={{ width: 18, height: 1, background: done ? "rgba(34,197,94,0.4)" : "rgba(255,255,255,0.08)", margin: "0 2px", transition: "background 0.3s" }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Offline */}
        {!isOnline && (
          <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 13px", marginBottom: 16, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", borderRadius: 9 }}>
            <span style={{ fontSize: 14 }}>📵</span>
            <span style={{ fontSize: 12, color: "#f87171", fontFamily: MONO }}>You are offline</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 13px", marginBottom: 16, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", borderRadius: 9 }}>
            <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
            <span style={{ fontSize: 12, color: "#f87171", fontFamily: MONO }}>{error}</span>
          </div>
        )}

        {/* ── Step 1: Access Key ──────────────────────────────── */}
        {step === "mfa" && (
          <form key="mfa" className="step-content" onSubmit={handleMfa} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", background: "rgba(139,92,246,0.07)", border: "1px solid rgba(139,92,246,0.2)", borderRadius: 10 }}>
              <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1.5 }}>🔐</span>
              <span style={{ fontSize: 11, fontFamily: MONO, color: "rgba(255,255,255,0.55)", lineHeight: 1.7 }}>
                {MFA_QUESTION}
              </span>
            </div>

            <div>
              <label style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", fontFamily: MONO, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>
                ACCESS KEY
              </label>
              <div style={{ position: "relative" }}>
                <input
                  ref={mfaRef}
                  type={showKey ? "text" : "password"}
                  className="admin-input"
                  value={mfaAnswer}
                  onChange={e => { setMfaAnswer(e.target.value); setError(""); }}
                  placeholder="••••••••••••"
                  autoComplete="off"
                  autoFocus
                  disabled={loading}
                  style={{ paddingRight: 42, letterSpacing: showKey ? "normal" : "0.15em" }}
                />
                <button type="button" onClick={() => setShowKey(v => !v)}
                  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.3)", padding: 2, lineHeight: 1 }}>
                  {showKey
                    ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading || !isOnline} style={{
              marginTop: 4,
              background: loading ? "rgba(124,58,237,0.35)" : "linear-gradient(135deg,#4c1d95,#7c3aed,#a855f7)",
              border: "none", borderRadius: 11, padding: "13px", color: "#fff",
              fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 14,
              cursor: (loading || !isOnline) ? "not-allowed" : "pointer",
              opacity: !isOnline ? 0.5 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              boxShadow: loading ? "none" : "0 4px 16px rgba(124,58,237,0.4)",
              transition: "opacity 0.2s",
            }}>
              {loading
                ? <><span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Checking…</>
                : "Continue →"}
            </button>
          </form>
        )}

        {/* ── Step 2: Email + Password ────────────────────────── */}
        {step === "credentials" && (
          <form key="credentials" className="step-content" onSubmit={handleCredentials} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", fontFamily: MONO, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>
                EMAIL
              </label>
              <input
                id="admin-email-input"
                type="email"
                className="admin-input"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(""); }}
                placeholder="admin@example.com"
                autoComplete="email"
                disabled={loading}
              />
            </div>

            <div>
              <label style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", fontFamily: MONO, letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>
                PASSWORD
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPw ? "text" : "password"}
                  className="admin-input"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(""); }}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  disabled={loading}
                  style={{ paddingRight: 42 }}
                />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.3)", padding: 2, lineHeight: 1 }}>
                  {showPw
                    ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading || !isOnline} style={{
              marginTop: 4,
              background: loading ? "rgba(124,58,237,0.35)" : "linear-gradient(135deg,#4c1d95,#7c3aed,#a855f7)",
              border: "none", borderRadius: 11, padding: "13px", color: "#fff",
              fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 14,
              cursor: (loading || !isOnline) ? "not-allowed" : "pointer",
              opacity: !isOnline ? 0.5 : 1,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              boxShadow: loading ? "none" : "0 4px 16px rgba(124,58,237,0.4)",
              transition: "opacity 0.2s",
            }}>
              {loading
                ? <><span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Signing in…</>
                : "Sign In"}
            </button>

            <button type="button" onClick={goBackToMfa}
              style={{ background: "none", border: "none", color: "rgba(255,255,255,0.25)", fontFamily: MONO, fontSize: 11, cursor: "pointer", textAlign: "center", padding: "4px 0" }}>
              ← back
            </button>
          </form>
        )}

        <div style={{ marginTop: 22, textAlign: "center", fontSize: 10, color: "rgba(255,255,255,0.12)", fontFamily: MONO, letterSpacing: "0.05em" }}>
          QASHUP · SYSTEM ADMINISTRATION
        </div>
      </div>
    </div>
  );
}
